import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc } from 'firebase/firestore';
import { db } from './firebase';
import './HOAllFranchisees.css';

function HOAllFranchisees({ onClose }) {
  const [franchisees, setFranchisees] = useState([]);
  const [allSchools, setAllSchools] = useState([]); // NEW: every school across every franchisee
  const [loading, setLoading] = useState(true);
  const [expandedFranchisee, setExpandedFranchisee] = useState(null);
  const [expandedSchool, setExpandedSchool] = useState(null);
  const [franchiseeData, setFranchiseeData] = useState({});
  const [loadingData, setLoadingData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('franchisees'); // franchisees, schools, students

  useEffect(() => {
    loadFranchisees();
    loadAllSchools(); // NEW: load schools on mount so we can search them
  }, []);

  // NEW: Pull every school once so we can match by school name in the search
  const loadAllSchools = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'schools'));
      const schoolsList = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        franchiseeId: d.data().franchiseeRef?.id || null
      }));
      setAllSchools(schoolsList);
    } catch (error) {
      console.error('Error loading all schools:', error);
    }
  };

  const loadFranchisees = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'Franchisees'));
      const franchiseesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by name
      franchiseesList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setFranchisees(franchiseesList);
    } catch (error) {
      console.error('Error loading franchisees:', error);
      alert('Error loading franchisees');
    } finally {
      setLoading(false);
    }
  };

  const loadFranchiseeDetails = async (franchiseeId) => {
    if (expandedFranchisee === franchiseeId) {
      setExpandedFranchisee(null);
      return;
    }

    setExpandedFranchisee(franchiseeId);
    
    // Check if we already have the data
    if (franchiseeData[franchiseeId]) {
      return;
    }

    setLoadingData(franchiseeId);

    try {
      const franchiseeRef = doc(db, 'Franchisees', franchiseeId);

      // Load schools
      const schoolsQuery = query(
        collection(db, 'schools'),
        where('franchiseeRef', '==', franchiseeRef)
      );
      const schoolsSnapshot = await getDocs(schoolsQuery);
      const schools = schoolsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      schools.sort((a, b) => (a.schoolName || '').localeCompare(b.schoolName || ''));

      // Load students
      const studentsQuery = query(
        collection(db, 'students'),
        where('franchiseeRef', '==', franchiseeRef)
      );
      const studentsSnapshot = await getDocs(studentsQuery);
      const students = studentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      students.sort((a, b) => (a.studentName || '').localeCompare(b.studentName || ''));

      // Group students by school
      const studentsBySchool = {};
      students.forEach(student => {
        const schoolId = student.schoolRef?.id || 'unknown';
        if (!studentsBySchool[schoolId]) {
          studentsBySchool[schoolId] = [];
        }
        studentsBySchool[schoolId].push(student);
      });

      setFranchiseeData(prev => ({
        ...prev,
        [franchiseeId]: {
          schools,
          students,
          studentsBySchool,
          activeStudents: students.filter(s => s.isActive).length,
          inactiveStudents: students.filter(s => !s.isActive).length,
          activeSchools: schools.filter(s => s.isActive).length
        }
      }));
    } catch (error) {
      console.error('Error loading franchisee details:', error);
    } finally {
      setLoadingData(null);
    }
  };

  const toggleSchool = (schoolId) => {
    setExpandedSchool(expandedSchool === schoolId ? null : schoolId);
  };

  // Calculate totals
  const calculateTotals = () => {
    let totalSchools = 0;
    let totalActiveStudents = 0;
    let totalInactiveStudents = 0;

    Object.values(franchiseeData).forEach(data => {
      totalSchools += data.schools?.length || 0;
      totalActiveStudents += data.activeStudents || 0;
      totalInactiveStudents += data.inactiveStudents || 0;
    });

    return { totalSchools, totalActiveStudents, totalInactiveStudents };
  };

  // UPDATED: Filter now matches franchisee name, email, OR school name
  const searchLower = searchTerm.toLowerCase().trim();

  // Build a map of franchiseeId -> [matching school names]
  // This is used both for filtering and for showing the "school match" badge
  const schoolMatchesByFranchisee = new Map();
  if (searchLower) {
    allSchools.forEach(school => {
      const schoolName = (school.schoolName || '').toLowerCase();
      if (schoolName.includes(searchLower) && school.franchiseeId) {
        if (!schoolMatchesByFranchisee.has(school.franchiseeId)) {
          schoolMatchesByFranchisee.set(school.franchiseeId, []);
        }
        schoolMatchesByFranchisee.get(school.franchiseeId).push(school.schoolName);
      }
    });
  }

  const filteredFranchisees = franchisees.filter(f => {
    if (!searchLower) return true;

    const nameMatch = (f.name || '').toLowerCase().includes(searchLower);
    const emailMatch = (f.email || '').toLowerCase().includes(searchLower);
    const schoolMatch = schoolMatchesByFranchisee.has(f.id);

    return nameMatch || emailMatch || schoolMatch;
  });

  const totals = calculateTotals();

  return (
    <div className="ho-franchisees-container">
      <div className="ho-franchisees-header">
        <h1>👥 All Franchisees</h1>
        <p className="subtitle">View all franchisees with their schools and students</p>
      </div>

      {/* Controls */}
      <div className="controls-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Search by franchisee name, email or school..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="view-toggle">
          <button 
            className={viewMode === 'franchisees' ? 'active' : ''}
            onClick={() => setViewMode('franchisees')}
          >
            👥 By Franchisee
          </button>
        </div>

        <button onClick={onClose} className="back-btn">← Back</button>
      </div>

      {/* NEW: Search results summary - only shown when actively searching */}
      {searchLower && (
        <div className="search-results-summary">
          Found <strong>{filteredFranchisees.length}</strong> franchisee{filteredFranchisees.length !== 1 ? 's' : ''}
          {schoolMatchesByFranchisee.size > 0 && (
            <> via <strong>{[...schoolMatchesByFranchisee.values()].flat().length}</strong> matching school{[...schoolMatchesByFranchisee.values()].flat().length !== 1 ? 's' : ''}</>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon">👥</div>
          <div className="card-content">
            <div className="card-label">Franchisees</div>
            <div className="card-value">{franchisees.length}</div>
          </div>
        </div>

        <div className="summary-card">
          <div className="card-icon">🏫</div>
          <div className="card-content">
            <div className="card-label">Total Schools</div>
            <div className="card-value">{allSchools.length || totals.totalSchools}</div>
          </div>
        </div>

        <div className="summary-card">
          <div className="card-icon">🎓</div>
          <div className="card-content">
            <div className="card-label">Active Students</div>
            <div className="card-value green">{totals.totalActiveStudents}</div>
          </div>
        </div>

        <div className="summary-card">
          <div className="card-icon">📋</div>
          <div className="card-content">
            <div className="card-label">Inactive Students</div>
            <div className="card-value orange">{totals.totalInactiveStudents}</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading franchisees...</div>
      ) : (
        <div className="franchisees-list">
          {filteredFranchisees.length === 0 ? (
            <div className="empty-state">No franchisees found</div>
          ) : (
            filteredFranchisees.map(franchisee => {
              const data = franchiseeData[franchisee.id];
              const isExpanded = expandedFranchisee === franchisee.id;
              const isLoading = loadingData === franchisee.id;
              const matchedSchools = schoolMatchesByFranchisee.get(franchisee.id); // NEW

              return (
                <div key={franchisee.id} className={`franchisee-card ${isExpanded ? 'expanded' : ''}`}>
                  <div 
                    className="franchisee-header"
                    onClick={() => loadFranchiseeDetails(franchisee.id)}
                  >
                    <div className="franchisee-info">
                      <h3>{franchisee.name || 'Unnamed Franchisee'}</h3>
                      <p className="email">{franchisee.email}</p>
                      {franchisee.phone && <p className="phone">📞 {franchisee.phone}</p>}
                      {/* NEW: Show which school matched the search */}
                      {matchedSchools && matchedSchools.length > 0 && (
                        <div className="school-match-badge">
                          🏫 School match: {matchedSchools.join(', ')}
                        </div>
                      )}
                    </div>

                    <div className="franchisee-stats">
                      {data && (
                        <>
                          <span className="stat schools">🏫 {data.schools?.length || 0}</span>
                          <span className="stat students">🎓 {data.activeStudents || 0}</span>
                        </>
                      )}
                      <span className="expand-icon">{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="franchisee-details">
                      {isLoading ? (
                        <div className="loading-detail">Loading schools and students...</div>
                      ) : data ? (
                        <>
                          {/* Stats Summary */}
                          <div className="detail-stats">
                            <div className="detail-stat">
                              <span className="label">Active Schools:</span>
                              <span className="value">{data.activeSchools}</span>
                            </div>
                            <div className="detail-stat">
                              <span className="label">Active Students:</span>
                              <span className="value green">{data.activeStudents}</span>
                            </div>
                            <div className="detail-stat">
                              <span className="label">Inactive Students:</span>
                              <span className="value orange">{data.inactiveStudents}</span>
                            </div>
                          </div>

                          {/* Schools List */}
                          <div className="schools-section">
                            <h4>🏫 Schools ({data.schools?.length || 0})</h4>
                            
                            {data.schools?.length === 0 ? (
                              <p className="no-data">No schools yet</p>
                            ) : (
                              <div className="schools-list">
                                {data.schools.map(school => {
                                  const schoolStudents = data.studentsBySchool[school.id] || [];
                                  const activeCount = schoolStudents.filter(s => s.isActive).length;
                                  const isSchoolExpanded = expandedSchool === school.id;
                                  // NEW: Highlight schools that match the search
                                  const isSchoolSearchMatch = searchLower &&
                                    (school.schoolName || '').toLowerCase().includes(searchLower);

                                  return (
                                    <div key={school.id} className={`school-item ${isSchoolExpanded ? 'expanded' : ''} ${isSchoolSearchMatch ? 'search-match' : ''}`}>
                                      <div 
                                        className="school-header"
                                        onClick={() => toggleSchool(school.id)}
                                      >
                                        <div className="school-info">
                                          <span className={`status-dot ${school.isActive ? 'active' : 'inactive'}`}></span>
                                          <span className="school-name">{school.schoolName}</span>
                                          <span className="school-code">({school.schoolCode})</span>
                                        </div>
                                        <div className="school-stats">
                                          <span className="student-count">
                                            {activeCount} student{activeCount !== 1 ? 's' : ''}
                                          </span>
                                          <span className="lesson-day">{school.lessonDay} {school.lessonTime}</span>
                                          <span className="expand-icon">{isSchoolExpanded ? '−' : '+'}</span>
                                        </div>
                                      </div>

                                      {isSchoolExpanded && (
                                        <div className="school-details">
                                          <div className="school-meta">
                                            <p>📧 {school.schoolEmail || school.contactEmail || 'No email'}</p>
                                            <p>📞 {school.schoolPhone || school.contactPhone || 'No phone'}</p>
                                            <p>💰 Term Fee: R{school.schoolFee?.termFee || 0}</p>
                                          </div>

                                          {schoolStudents.length > 0 ? (
                                            <table className="students-table">
                                              <thead>
                                                <tr>
                                                  <th>Student</th>
                                                  <th>Account #</th>
                                                  <th>Class</th>
                                                  <th>Status</th>
                                                  <th>Contact</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {schoolStudents.map(student => (
                                                  <tr key={student.id} className={!student.isActive ? 'inactive' : ''}>
                                                    <td>{student.studentName}</td>
                                                    <td>{student.accountNumber}</td>
                                                    <td>{student.class || '-'}</td>
                                                    <td>
                                                      <span className={`status-badge ${student.isActive ? 'active' : 'inactive'}`}>
                                                        {student.isActive ? 'Active' : 'Inactive'}
                                                      </span>
                                                    </td>
                                                    <td>{student.primaryContact?.email || '-'}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          ) : (
                                            <p className="no-students">No students at this school</p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default HOAllFranchisees;